/**
 * Utilities for optimizing Supabase Storage images via wsrv.nl proxy
 * wsrv.nl provides free image resizing, WebP conversion, and caching
 */

interface ImageTransformOptions {
  width?: number;
  height?: number;
  quality?: number;
  format?: 'webp' | 'avif' | 'origin';
}

/**
 * Generates an optimized image URL via wsrv.nl proxy.
 * Only applies to Supabase Storage URLs; other URLs are returned as-is.
 */
export const getOptimizedImageUrl = (
  originalUrl: string,
  options: ImageTransformOptions = {}
): string => {
  if (!originalUrl) return '';

  // Only proxy Supabase Storage URLs
  if (!originalUrl.includes('supabase.co/storage')) {
    return originalUrl;
  }

  const { width, height, quality = 75, format = 'webp' } = options;

  const params = new URLSearchParams();
  params.set('url', originalUrl);
  if (width) params.set('w', String(width));
  if (height) params.set('h', String(height));
  params.set('q', String(quality));
  params.set('output', format);
  params.set('fit', 'cover');

  return `https://wsrv.nl/?${params.toString()}`;
};

/**
 * Generates srcset for responsive images with multiple sizes
 */
export const generateResponsiveSrcSet = (
  originalUrl: string,
  sizes: number[] = [400, 800, 1200]
): string => {
  if (!originalUrl || !originalUrl.includes('supabase.co/storage')) {
    return '';
  }

  return sizes
    .map(size => {
      const url = getOptimizedImageUrl(originalUrl, { width: size });
      return `${url} ${size}w`;
    })
    .join(', ');
};

/**
 * Default sizes for common use cases
 */
export const IMAGE_SIZES = {
  thumbnail: { width: 250, height: 250 },
  card: { width: 400, height: 400 },
  hero: { width: 1200, height: 500 },
  logo: { width: 320, height: 160 },
} as const;
